import React, {useEffect, useState} from 'react';
import {Stack, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {useSnackbar} from 'notistack';
import {
  Execution,
  ExecutionFilters,
  ExecutionLight,
  executionsService,
  ExecutionStatus,
} from '@store/operations/executions';

import ModalComponent from '@components/modal/Modal.component';

import COLUMN_TYPES from '@utils/table/columnTypes.table';
import {GridSortItem, GridValueGetterParams} from '@mui/x-data-grid-pro';
import NewTableComponent from '@components/table/NewTable.component';
import dayjs from 'dayjs';
import {LoadingButton} from '@mui/lab';
import {ExecutionLinesFilters} from '@store/invoices/executionLines';
import {TablePagination} from '@store/common/pagination.model';

interface ExecutionReceiptModalModalProps {
  handleClose: () => void;
  onSave: () => void;
  sortModel: GridSortItem;
  tablePagination: TablePagination;
  filters: ExecutionFilters | ExecutionLinesFilters;
  isInvoicing?: boolean;
}

const ExecutionReceiptModal = (props: ExecutionReceiptModalModalProps) => {
  const {handleClose, sortModel, tablePagination, filters, onSave} = props;

  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();

  const [loading, setLoading] = useState(false);

  const [pagination, setPagination] = useState<TablePagination>(tablePagination);
  const [sort, setSort] = useState<GridSortItem>(sortModel);


  const [updatedExecutions, setUpdatedExecutions] = useState<Partial<ExecutionLight>[]>();
  const [totalExecutions, setTotalExecutions] = useState<number>(0);
  const [selectedUpdatedExecutions, setSelectedUpdatedExecutions] = useState<number[]>([]);

  const updatePagination = (name: string) => (value: number) => {
    setPagination((state) => ({...state, [name]: value}));
  };

  useEffect(() => {
    const customFilters = {
      from: filters.from,
      to: filters.to,
      search: filters.search,
      tableFilters: filters.tableFilters,
    };
    setLoading(true);
    executionsService.getExecutionsWithParams(customFilters, sort, pagination).subscribe((data) => {
      setLoading(false);
      setUpdatedExecutions(data.data);
      setTotalExecutions(data.total);
    });
  }, [pagination, sort, filters]);

  const handleSave = () => {
    setLoading(true);
    const executionsToSave = updatedExecutions?.filter((exe) => selectedUpdatedExecutions.includes(exe.id!));
    if (executionsToSave) {
      const filtered = executionsToSave.map((exe) => {
        return {
          status: ExecutionStatus.EXECUTED,
          quantity1: exe.quantity1,
          quantity2: exe.quantity2,
          executedAt: exe.executedAt,
          id: exe.id
        };
      });
      executionsService.updatesMultiplesExecution(filtered).subscribe({
        error: (err) => enqueueSnackbar(err.text, err.options),
        next: () => {
          setLoading(false);

          enqueueSnackbar(t('executions.success.update'), {variant: 'success'});
          onSave();
        }
      });
    }
  };
  return (
    <ModalComponent
      aria-label="small-padding"
      handleClose={handleClose}
      maxWidth={false}
      title={t('operations.batchExecutions.fillExecutionsData')}
      content={
        <Stack pt="20px" spacing={2} direction="column" width="100%" height="85vh">
          <Stack flex={1} width="100%" spacing={2}>
            <NewTableComponent
              editableFields={['execution.executedAt', 'execution.quantity1', 'execution.quantity2']}
              loading={loading}
              columns={[
                COLUMN_TYPES.TEXT({
                  field: 'execution.plannedFrom',
                  color: 'secondary',
                  label: t('operations.executions.table.plannedFrom'),
                  valueGetter: (params: GridValueGetterParams<any, ExecutionLight>) => params.row.plannedFrom,
                  valueFormatter: (params) => params.value ? t('global.date', {date: new Date(params.value)}) : '',
                  width: 130,
                }),
                COLUMN_TYPES.DATE({
                  field: 'executedAt',
                  color: 'secondary',
                  label: t('operations.executions.table.executedAt'),
                  valueGetter: (params: GridValueGetterParams<any, ExecutionLight>) => params.row.executedAt ?? params.row.plannedFrom,
                  width: 170,
                }),
                COLUMN_TYPES.TEXT({
                  field: 'name',
                  color: 'secondary',
                  label: t('operations.executions.table.name'),
                  valueGetter: (params: GridValueGetterParams<any, ExecutionLight>) => params.row.name,
                  width: 300,
                }),
                COLUMN_TYPES.NUMBER({
                  field: 'quantity1',
                  label: t('operations.executions.table.quantity1'),
                  valueGetter: (params: GridValueGetterParams<any, ExecutionLight>) => params.row.quantity1,
                  endAdornment: (row: ExecutionLight) => <Typography fontSize={8}
                                                                     fontWeight="400">{row.unit1}</Typography>,
                  width: 100,
                }),
                COLUMN_TYPES.NUMBER({
                  field: 'quantity2',
                  label: t('operations.executions.table.quantity2'),
                  valueGetter: (params: GridValueGetterParams<any, ExecutionLight>) => params.row.quantity2 ?? 0,
                  endAdornment: (row: ExecutionLight) => <Typography fontSize={8}
                                                                     fontWeight="400">{row.unit2}</Typography>,
                  width: 100,
                }),
                COLUMN_TYPES.TEXT_BOLD({
                  field: 'type',
                  label: t('operations.executions.table.type'),
                  valueGetter: (params: GridValueGetterParams<any, ExecutionLight>) => params.row.type,
                  fullWidth: true,
                  minWidth: 250,
                }),
                COLUMN_TYPES.SELECT({
                  field: 'execution.status',
                  label: t('global.status'),
                  options: ExecutionStatus.selectItems(),
                  sortable: true,
                  valueGetter: (params: GridValueGetterParams<any, Execution>) => params.row.status,
                  width: 150,
                }),
                COLUMN_TYPES.TEXT({
                  field: 'waste',
                  color: 'secondary',
                  label: t('operations.executions.table.waste'),
                  valueGetter: (params: GridValueGetterParams<any, ExecutionLight>) => params.row.waste,
                  sortable: true,
                  width: 250,
                }),
                COLUMN_TYPES.TEXT({
                  field: 'container',
                  color: 'secondary',
                  label: t('operations.executions.table.container'),
                  valueGetter: (params: GridValueGetterParams<any, ExecutionLight>) => params.row.container,
                  fullWidth: true,
                  minWidth: 250,
                }),
                COLUMN_TYPES.TEXT({
                  field: 'clientOrganisationUnit.name',
                  label: t('operations.executions.table.clientUnitName'),
                  valueGetter: (params: GridValueGetterParams<any, ExecutionLight>) => params.row.clientOrganisationUnit.name,
                  fullWidth: true,
                  minWidth: 250,
                }),
                COLUMN_TYPES.TEXT({
                  field: 'supplierOrganisationUnit.name',
                  label: t('operations.executions.table.supplierOrganisationUnitName'),
                  valueGetter: (params: GridValueGetterParams<any, ExecutionLight>) => params.row.supplierOrganisationUnit.name,
                  fullWidth: true,
                  minWidth: 250,
                }),
              ]}
              disableRowSelectionOnClick={true}
              handleRowSelected={setSelectedUpdatedExecutions}
              rowsSelected={selectedUpdatedExecutions}
              handlePageSizeChange={updatePagination('limit')}
              pageSize={pagination.limit}
              handlePageChange={(page) => updatePagination('page')(page + 1)}
              page={pagination.page - 1}
              handleSortModelChange={setSort}
              sortModel={[sortModel]}
              totalRows={totalExecutions}
              handleRowChanged={(execution: any) => {
                const updatedExecution = {
                  executedAt: dayjs(execution.executedAt).isValid() ? execution.executedAt : undefined,
                  quantity1: execution.quantity1,
                  quantity2: execution.quantity2,
                  status: ExecutionStatus.EXECUTED,
                };
                setUpdatedExecutions((state) =>
                  state?.map((e) =>
                    execution && e.id === execution.id ? {...e, ...updatedExecution} : e
                  )
                );
                setSelectedUpdatedExecutions((state) =>
                  state.includes(execution.id) ? state : [...state, execution.id]
                );
                return {...execution, ...updatedExecution};
              }}
              data={updatedExecutions}
            />
          </Stack>
        </Stack>
      }
      actions={
        <>
          <LoadingButton
            variant="contained"
            color="primary"
            loading={loading}
            disabled={selectedUpdatedExecutions.length === 0}
            onClick={handleSave}
          >
            {t('global.validate')}
          </LoadingButton>
        </>
      }
    />
  );
};

export default ExecutionReceiptModal;
